import React, {useCallback, useEffect, useState} from 'react';
import {Download, FileText, RefreshCw} from 'lucide-react';
import {Spin} from 'antd';
import {APIv1} from '../../../../api';

const DeviceLogsSection = ({serialNumber, token}) => {
    const [deviceLogs, setDeviceLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    const fetchDeviceLogs = useCallback(async () => {
        if (!serialNumber || !token) {
            return;
        }

        setLogsLoading(true);

        try {
            const response = await APIv1.get(`/device/log/list/?dsn=${encodeURIComponent(serialNumber)}`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });

            setDeviceLogs(response.data || []);
        } catch (err) {
            console.error('Device logs request failed:', err);
            setDeviceLogs([]);
        } finally {
            setLogsLoading(false);
        }
    }, [serialNumber, token]);

    useEffect(() => {
        fetchDeviceLogs();
    }, [fetchDeviceLogs]);

    const formatFileSize = (bytes) => {
        if (!bytes && bytes !== 0) {
            return "----";
        }

        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getLogFileUrl = (fileUrl) => {
        if (!fileUrl) {
            return null;
        }

        const markdownLinkMatch = fileUrl.match(/\]\((https?:\/\/.+)\)$/);

        return markdownLinkMatch?.[1] || fileUrl;
    };

    const getLogDownloadFileName = (log) => {
        const logSerialNumber = log.device_serial_number || serialNumber || "unknown";
        const logDate = log.log_date || "unknown-date";
        const extension = log.file_name?.includes(".")
            ? `.${log.file_name.split(".").pop()}`
            : ".log";

        return `device-logs_${logSerialNumber}_${logDate}${extension}`;
    };

    const renderLogs = () => {
        if (logsLoading) {
            return (
                <div className="detail-view__logs-loading">
                    <Spin size="small"/>
                </div>
            );
        }

        if (!deviceLogs.length) {
            return (
                <div className="detail-view__logs-empty">
                    No logs found.
                </div>
            );
        }

        return (
            <div className="detail-view__logs-list">
                {deviceLogs.map(log => {
                    const fileUrl = getLogFileUrl(log.file_url);
                    const downloadFileName = getLogDownloadFileName(log);

                    return (
                        <div className="detail-view__log-item" key={log.id || log.file_key}>
                            <div className="detail-view__log-main">
                                <span className="detail-view__log-icon"><FileText size={16}/></span>
                                <div className="detail-view__log-meta">
                                    <span className="detail-view__log-name">
                                        {log.file_name || "----"}
                                    </span>
                                    <span className="detail-view__log-date">{log.log_date || "----/--/--"}</span>
                                </div>
                            </div>
                            <div className="detail-view__log-actions">
                                <span className="detail-view__log-size">{formatFileSize(log.file_size)}</span>
                                {fileUrl && (
                                    <a
                                        className="detail-view__log-download"
                                        href={fileUrl}
                                        download={downloadFileName}
                                        rel="noreferrer"
                                        title="Download log"
                                    >
                                        <Download size={16}/>
                                    </a>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <section className="detail-view__section detail-view__section--logs">
            <div className="detail-view__section-header detail-view__section-header--with-actions">
                <span className="detail-view__section-index">06</span>
                <div className="detail-view__section-heading">
                    <span className="detail-view__section-icon"><FileText size={16}/></span>
                    <h2 className="detail-view__section-title">Logs</h2>
                </div>
                <button
                    className="detail-view__logs-reload"
                    type="button"
                    onClick={fetchDeviceLogs}
                    disabled={logsLoading}
                    title="Reload logs"
                    aria-label="Reload logs"
                >
                    <RefreshCw size={16}/>
                </button>
            </div>
            {renderLogs()}
        </section>
    );
};

export default DeviceLogsSection;
